import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  BooleanInput,
  Form,
  Loading,
  SelectInput,
  TextInput,
  required,
  useDataProvider,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { RequireAccess } from '../auth/RequireAccess';
import type { AdminDataProvider, CannedResponse } from '../dataProvider';
import {
  MACRO_ACTION_OPTIONS,
  TEMPLATE_VARIABLES,
} from './cannedResponsesState';

/**
 * Edit a canned response template (D-canned-responses). Name, body, kind and the bundled
 * macro action are editable, plus the ACTIVE toggle: the soft-disable control that RETIRES
 * a template (active=false), the only way to take one out of service. There is NO delete
 * button: a retired template stays referenceable in history.
 *
 * Audit for free: saving calls `dataProvider.update('cannedResponses')`; the wrapper
 * records the diff (incl. active: true → false on a retire). The route is gated
 * `canned_responses:write` (super_admin only).
 */

const KIND_CHOICES = [
  { id: 'response', name: 'Response' },
  { id: 'macro', name: 'Macro' },
];

const MACRO_CHOICES = MACRO_ACTION_OPTIONS.map((o) => ({
  id: o.value,
  name: o.label,
}));

const VARIABLE_HINT = `Variables: ${TEMPLATE_VARIABLES.map((v) => `{${v}}`).join(' ')}`;

interface CannedEditValues {
  name: string;
  body: string;
  kind: 'response' | 'macro';
  macroAction: string;
  active: boolean;
}

/** The current template's macro option value, or the resolve default. */
const macroValueOf = (record: CannedResponse): string =>
  MACRO_ACTION_OPTIONS.find(
    (o) => o.action.toStatus === record.action?.toStatus,
  )?.value ?? 'resolve';

const MacroActionInput = () => {
  const kind = useWatch({ name: 'kind' });
  if (kind !== 'macro') return null;
  return (
    <SelectInput
      source="macroAction"
      label="Bundled action"
      choices={MACRO_CHOICES}
      validate={required('A macro needs a bundled action')}
    />
  );
};

const CannedResponseEditForm = ({ record }: { record: CannedResponse }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const defaultValues: CannedEditValues = {
    name: record.name,
    body: record.body,
    kind: record.kind,
    macroAction: macroValueOf(record),
    active: record.active,
  };

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as CannedEditValues;
    const action =
      values.kind === 'macro'
        ? (MACRO_ACTION_OPTIONS.find((o) => o.value === values.macroAction)
            ?.action ?? null)
        : null;
    const data = {
      name: values.name,
      body: values.body,
      kind: values.kind,
      action,
      active: values.active,
    };
    const previousData = {
      name: record.name,
      body: record.body,
      kind: record.kind,
      action: record.action,
      active: record.active,
    };
    try {
      if (JSON.stringify(data) !== JSON.stringify(previousData)) {
        await dataProvider.update('cannedResponses', {
          id: record.id,
          data,
          previousData,
        });
      }
      notify('Template updated', { type: 'success' });
      redirect('list', 'cannedResponses');
    } catch {
      notify('Could not save the template', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Edit {record.name}
      </Typography>
      <Form defaultValues={defaultValues} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextInput
              source="name"
              label="Name"
              validate={required('Name is required')}
              fullWidth
            />
            <SelectInput
              source="kind"
              label="Kind"
              choices={KIND_CHOICES}
              validate={required('Kind is required')}
            />
            <MacroActionInput />
            <TextInput
              source="body"
              label="Body"
              validate={required('Body is required')}
              helperText={VARIABLE_HINT}
              multiline
              minRows={3}
              fullWidth
            />
            {/* The soft-disable control — retire/reinstate (no delete). */}
            <BooleanInput source="active" label="Active" />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Save
          </Button>
          <Button
            type="button"
            onClick={() => redirect('list', 'cannedResponses')}
          >
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** The edit view. Exported for testing without the access wrapper. */
export const CannedResponseEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<CannedResponse>(
    'cannedResponses',
    { id: Number(id) },
  );
  if (isLoading || !record) return <Loading />;
  return <CannedResponseEditForm record={record} />;
};

/** Gated entry point registered as the `cannedResponses` Resource's edit. */
export const CannedResponseEdit = () => (
  <RequireAccess capability="canned_responses:write">
    <CannedResponseEditView />
  </RequireAccess>
);
